import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertTriangle,
  FileText,
  ShieldCheck,
  Lock,
  Bell,
  FlaskConical,
  CircleCheck,
  HeartPulse,
  Check,
  X,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatCard } from "./shared";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { QUERY_KEYS } from "@/hooks/use-queries";

interface ComplianceWarning {
  id: number;
  type: string;
  actor: string;
  description: string;
  timestamp: string | null;
  severity: string;
}

function SeverityBadge({ severity }: { severity: string }) {
  if (severity === "critical") return <Badge variant="destructive">critical</Badge>;
  if (severity === "warning") return <Badge variant="secondary">warning</Badge>;
  return <Badge variant="outline">{severity}</Badge>;
}

export function ComplianceDashboard({ auditLogs, users }: { auditLogs: any[]; users: any[] }) {
  const [includeTestAccounts, setIncludeTestAccounts] = useState(false);
  const [reviewingRequest, setReviewingRequest] = useState<any | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const { toast } = useToast();

  const { data: hardshipRequests = [], isLoading: hardshipLoading } = useQuery<any[]>({
    queryKey: QUERY_KEYS.hardshipAssistanceRequests,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "approved" | "denied" }) => {
      const res = await apiRequest("PATCH", `/api/hardship-assistance/${id}/status`, {
        status,
        adminNote: reviewNote,
      });
      return res.json();
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.hardshipAssistanceRequests });
      setReviewingRequest(null);
      setReviewNote("");
      toast({
        title: status === "approved" ? "Request Approved" : "Request Denied",
        description: `The hardship assistance request has been ${status}.`,
      });
    },
    onError: () => {
      toast({ title: "Action Failed", description: "Could not update the request. Please try again.", variant: "destructive" });
    },
  });

  const { data: warnings = [], isLoading: warningsLoading } = useQuery<ComplianceWarning[]>({
    queryKey: ["/api/admin/compliance", includeTestAccounts],
    queryFn: async () => {
      const url = `/api/admin/compliance${includeTestAccounts ? "?includeTest=true" : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch compliance warnings");
      return res.json();
    },
  });

  const criticalCount = warnings.filter((w) => w.severity === "critical").length;
  const warningCount = warnings.filter((w) => w.severity === "warning").length;

  const todayLogs = auditLogs.filter((l: any) => {
    const today = new Date().toDateString();
    return l.timestamp && new Date(l.timestamp).toDateString() === today;
  });

  const actionCounts: Record<string, number> = {};
  auditLogs.forEach((l: any) => {
    const action = l.action || "unknown";
    actionCounts[action] = (actionCounts[action] || 0) + 1;
  });
  const topActions = Object.entries(actionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <>
      {/* Header row with toggle */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Compliance Warnings</h3>
          <p className="text-xs text-muted-foreground/70 mt-0.5">
            {includeTestAccounts
              ? "Showing all events including test accounts"
              : "Test-account events hidden by default"}
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="compliance-test-toggle-row">
          <FlaskConical className="w-4 h-4 text-muted-foreground" />
          <Label htmlFor="include-test-accounts" className="text-sm cursor-pointer select-none">
            Include test accounts
          </Label>
          <Switch
            id="include-test-accounts"
            checked={includeTestAccounts}
            onCheckedChange={setIncludeTestAccounts}
            data-testid="switch-include-test-accounts"
          />
        </div>
      </div>

      {/* Stat cards driven by deduplicated warnings from the API */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Critical Events"
          value={warningsLoading ? "—" : criticalCount}
          description="Requires immediate attention"
          icon={AlertTriangle}
          color="red"
        />
        <StatCard
          title="Warnings"
          value={warningsLoading ? "—" : warningCount}
          description="Review recommended"
          icon={AlertTriangle}
          color="yellow"
        />
        <StatCard
          title="Wallet Events Today"
          value={todayLogs.filter((l: any) =>
            l.action?.includes("wallet") ||
            l.action?.includes("contribution") ||
            l.action?.includes("transaction")
          ).length}
          description="Wallet-related events today"
          icon={FileText}
          color="blue"
        />
        <StatCard
          title="Data Security"
          value="98%"
          description="Overall compliance score"
          icon={ShieldCheck}
          color="green"
        />
      </div>

      {/* Main warnings table */}
      <Card data-testid="compliance-warnings-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Active Compliance Warnings
          </CardTitle>
          <CardDescription>
            Warning and critical events filtered from the audit log
          </CardDescription>
        </CardHeader>
        <CardContent>
          {warningsLoading ? (
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-4 px-3 py-2">
                {[48, 64, 100, 40, 52].map((w, i) => (
                  <Skeleton key={i} className="h-3.5" style={{ width: w }} />
                ))}
              </div>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="grid grid-cols-5 gap-4 px-3 py-3 rounded-lg border items-center">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
              ))}
            </div>
          ) : warnings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-center" data-testid="compliance-empty-state">
              <CircleCheck className="w-10 h-10 text-green-500" />
              <p className="font-medium text-green-700 dark:text-green-400">No compliance issues</p>
              <p className="text-sm text-muted-foreground">
                {includeTestAccounts
                  ? "No warning or critical events in the audit log"
                  : "No real-account warnings found — toggle to include test accounts"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="compliance-warnings-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Severity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {warnings.map((warning) => (
                    <TableRow key={warning.id} data-testid={`warning-row-${warning.id}`}>
                      <TableCell className="font-medium whitespace-nowrap">
                        {warning.type}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate">
                        {warning.actor}
                      </TableCell>
                      <TableCell className="text-sm max-w-[240px] truncate">
                        {warning.description || "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {warning.timestamp
                          ? format(new Date(warning.timestamp), "MMM d, yyyy HH:mm")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <SeverityBadge severity={warning.severity} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit summary section */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              Data Access Summary
            </CardTitle>
            <CardDescription>All audit log actions by type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topActions.map(([action, count]) => (
                <div
                  key={action}
                  className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/50"
                  data-testid={`audit-action-${action}`}
                >
                  <span className="text-sm truncate flex-1">{action}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
              {topActions.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No audit logs recorded</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Contribution Audits
            </CardTitle>
            <CardDescription>Recent contribution and wallet access events</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-3">
                {todayLogs.slice(0, 10).map((log: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                    data-testid={`audit-log-${idx}`}
                  >
                    <Badge
                      variant={
                        log.severity === "critical"
                          ? "destructive"
                          : log.severity === "warning"
                          ? "secondary"
                          : "outline"
                      }
                    >
                      {log.severity || "info"}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{log.action}</p>
                      <p className="text-xs text-muted-foreground truncate">{log.details}</p>
                    </div>
                  </div>
                ))}
                {todayLogs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No events logged today</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Hardship Assistance Requests */}
      <Card data-testid="card-hardship-assistance-requests">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HeartPulse className="w-5 h-5" />
            Hardship Assistance Requests
          </CardTitle>
          <CardDescription>
            Review and act on patient hardship assistance requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hardshipLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-4 p-3 rounded-lg border items-center">
                  <Skeleton className="h-4 w-40 flex-1" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : hardshipRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center" data-testid="hardship-empty-state">
              <CircleCheck className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No hardship assistance requests submitted yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="table-hardship-requests">
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hardshipRequests.map((r: any) => (
                    <TableRow key={r.id} data-testid={`hardship-row-${r.id}`}>
                      <TableCell className="text-sm font-medium max-w-[120px] truncate">
                        {r.userId}
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">
                        {r.reason}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        ${(r.amountRequestedCents / 100).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {r.createdAt ? format(new Date(r.createdAt), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell>
                        {r.status === "approved" ? (
                          <Badge variant="default">Approved</Badge>
                        ) : r.status === "denied" ? (
                          <Badge variant="destructive">Denied</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status === "pending" ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setReviewingRequest(r); setReviewNote(""); }}
                              data-testid={`button-review-hardship-${r.id}`}
                            >
                              Review
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {r.reviewedBy && `by ${r.reviewedBy}`}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!reviewingRequest} onOpenChange={(open) => { if (!open) { setReviewingRequest(null); setReviewNote(""); } }}>
        <DialogContent data-testid="dialog-review-hardship">
          <DialogHeader>
            <DialogTitle>Review Hardship Request</DialogTitle>
            <DialogDescription>
              {reviewingRequest && (
                <>
                  <strong>{reviewingRequest.reason}</strong>
                  {" — "}${(reviewingRequest.amountRequestedCents / 100).toFixed(2)} requested
                  {reviewingRequest.supportingDetails && (
                    <p className="mt-1 text-xs">{reviewingRequest.supportingDetails}</p>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="admin-note">Admin Note <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Textarea
                id="admin-note"
                placeholder="Add a note for the patient..."
                rows={3}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                data-testid="textarea-admin-note"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => { setReviewingRequest(null); setReviewNote(""); }}
                data-testid="button-review-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => reviewMutation.mutate({ id: reviewingRequest!.id, status: "denied" })}
                disabled={reviewMutation.isPending}
                data-testid="button-review-deny"
              >
                <X className="w-4 h-4 mr-1" />
                Deny
              </Button>
              <Button
                onClick={() => reviewMutation.mutate({ id: reviewingRequest!.id, status: "approved" })}
                disabled={reviewMutation.isPending}
                data-testid="button-review-approve"
              >
                <Check className="w-4 h-4 mr-1" />
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alert configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Alert Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>Contribution Verification Alerts</Label>
                <Switch checked={true} data-testid="switch-contribution-alerts" />
              </div>
              <p className="text-xs text-muted-foreground">Notify when contributions exceed threshold</p>
            </div>
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>Access Control Alerts</Label>
                <Switch checked={true} data-testid="switch-access-alerts" />
              </div>
              <p className="text-xs text-muted-foreground">Alert on unauthorized access attempts</p>
            </div>
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label>SLA Breach Alerts</Label>
                <Switch checked={true} data-testid="switch-sla-alerts" />
              </div>
              <p className="text-xs text-muted-foreground">Notify when service SLA is at risk</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
